import { useState } from "react";
import classNames from "classnames";
import ObjectsBox from "../Objects";

import { copyToClipboard } from "helpers/utility/utilities";
import TooltipWrapper from "views/uikit/TooltipWrapper";
import LinkIcon from "assets/icons/LinkIcon";

import styles from "./style.module.scss";

type ImageInfo = {
  Height?: number;
  Width?: number;
  "File size"?: string;
  "Mime type"?: string;
};

const GeneralBox = ({
  img,
  imageLabels,
  objectLables,
  selectedLabels,
  uniquenessData,
  metadataLinks,
}: any) => {
  const [copiedText, setCopiedText] = useState("");
  const imageUniquenessScore: number | null =
    uniquenessData?.find(
      (singleData: any) => !singleData.objectId && singleData.imageId
    )?.uniquenessScore || null;

  const objectUniquenessScores: any[] =
    uniquenessData
      ?.filter((data: any) => !!data.objectId)
      .map((data: any) => {
        return {
          ...data,
          uniquenessScore: data.uniquenessScore,
        };
      }) || [];

  let imgInfo: ImageInfo = {
    Height: img?.height,
    Width: img?.width,
    "File size": img?.file_size_display_value,
    "Mime type": img?.mime_type,
  };

  const copyIconClassnames = classNames(styles.copyIcon, {
    [styles.defaultCursor]: copiedText,
  });

  const groupedInfo: { key: string; value: string | number }[][] =
    Object.entries(imgInfo).reduce(
      (
        acc: { key: string; value: string | number }[][],
        [key, value],
        index
      ) => {
        const pairIndex = Math.floor(index / 2);
        acc[pairIndex] = acc[pairIndex] || [];
        acc[pairIndex].push({ key, value });
        return acc;
      },
      []
    );

  const isSmalltext = (key: string) => {
    let value = key?.toLowerCase();
    switch (value) {
      case "file name":
        return true;
      case "file size":
        return true;
      case "height":
        return true;
      default:
        return false;
    }
  };

  const handleCopyUrlClick = (text: string) => {
    copyToClipboard(text)
      .then(() => {
        setCopiedText(text);
        setTimeout(() => setCopiedText(""), 3000);
      })
      .catch((err) => console.error(err));
  };

  return (
    <div className={styles.generalContainer}>
      {img && (
        <div className={styles.infoContainer}>
          <div className={styles.infoRow}>
            <div className={styles.pathPair}>
              <span className={styles.path}>Path</span>
              <span className={styles.pathText} title={img?.original_uri}>
                <span className={styles.imageUri}>{img?.original_uri}</span>

                <div
                  className={copyIconClassnames}
                  data-tooltip-id={"image-view-url-button-tooltip"}
                  data-tooltip-content={copiedText ? "Copied!" : "Copy URL"}
                  title=""
                  onClick={() => handleCopyUrlClick(img.original_uri || "")}
                >
                  <LinkIcon clicked={copiedText === img.original_uri} />

                  <TooltipWrapper id="image-view-url-button-tooltip" />
                </div>
              </span>
            </div>
          </div>
          {groupedInfo.map((pair, index) => (
            <div className={styles.infoRow} key={index}>
              {pair.map(({ key, value }, index) => (
                <div className={styles.pair} key={index}>
                  <span
                    className={styles.infoTitle}
                    style={
                      isSmalltext(key) ? { width: "60px" } : { width: "76px" }
                    }
                  >
                    {key}
                  </span>
                  <span className={styles.infoDescription}>{value}</span>
                </div>
              ))}
            </div>
          ))}
          {typeof imageUniquenessScore === "number" && (
            <div className={styles.infoRow}>
              <div className={classNames(styles.pair, styles.fullWidth)}>
                <span className={styles.infoTitle}>Uniqueness Score</span>
                <span className={styles.infoDescription}>
                  {imageUniquenessScore}
                </span>
              </div>
            </div>
          )}
          {metadataLinks.length > 0 &&
            metadataLinks.map((metadataLink: string, index: number) => {
              const infoTitle = `Link${
                metadataLinks.length > 1 ? ` ${index + 1}` : ""
              }`;

              return (
                <div className={styles.infoRow} key={`metadata-link-${index}`}>
                  <div className={classNames(styles.pair, styles.fullWidth)}>
                    <span className={styles.infoTitle}>{infoTitle}</span>
                    <div className={styles.linkContainer}>
                      <span
                        className={styles.metadataLink}
                        title={metadataLink}
                      >
                        {metadataLink}
                      </span>
                      <div
                        className={copyIconClassnames}
                        data-tooltip-id={"image-view-url-button-tooltip"}
                        data-tooltip-content={
                          copiedText ? "Copied!" : "Copy URL"
                        }
                        title=""
                        onClick={() => handleCopyUrlClick(metadataLink)}
                      >
                        <LinkIcon clicked={copiedText === metadataLink} />

                        <TooltipWrapper id="image-view-url-button-tooltip" />
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
        </div>
      )}
      {imageLabels?.length > 0 && (
        <div className={styles.imageLabelsContainer}>
          <div className={styles.imageLabelTitle}>Image labels</div>
          <div className={styles.imageLabels}>
            {imageLabels?.map((singleLabel: string, index: number) => (
              <div key={index} className={styles.imageLabel}>
                {singleLabel}
              </div>
            ))}
          </div>
        </div>
      )}
      {objectLables && objectLables.length > 0 && (
        <div className={styles.objectLabelsContainer}>
          <div className={styles.objectLabelTitle}>Objects</div>
          <ObjectsBox
            labels={objectLables}
            selectedLabels={selectedLabels}
            uniquenessScores={objectUniquenessScores}
          />
        </div>
      )}
    </div>
  );
};

export default GeneralBox;
